import { useStorage } from "@vueuse/core"
import { Marked, Renderer } from "marked"
import { z } from "zod"

export const ReleaseMetaSchema = z.object({
  title: z.string().min(1),
  // Strict YYYY-MM-DD so dates sort/compare lexically and always parse to a valid Date.
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  tags: z.array(z.string()).optional(),
})

export type ReleaseMeta = z.infer<typeof ReleaseMetaSchema>

export interface ReleaseItem {
  /**
   * Stable public identifier, taken verbatim from the note's filename without its extension
   * (`releases/2026-07-26-statut-sinistre.md` → `2026-07-26-statut-sinistre`). Encoded in the
   * `?release=` query param of /releases to deep-link an unfolded note, so **renaming a release
   * file breaks any link previously shared for it**.
   */
  slug: string
  label: string
  meta: ReleaseMeta
  /** Release body rendered from markdown to HTML, injected via `v-html`. */
  html: string
}

// Eagerly inlined as raw strings at build time by Vite. Relative path so no `~` alias is
// needed (pure SPA). No server / runtime fetch involved.
const modules = import.meta.glob("../releases/*.md", {
  query: "?raw",
  import: "default",
  eager: true,
}) as Record<string, string>

const IMAGE_WIDTH_HINT = /^width=(\d+)$/

/**
 * Markdown renderer for release bodies. An image title of the form `width=<px>` is not rendered as a
 * tooltip but as the image's display width: `![alt](url "width=520")`. Retina captures of small
 * panels otherwise stretch to the full column, twice as large as they appear on screen. The CSS
 * `max-width: 100%` still shrinks them on narrow viewports.
 */
const releaseMarked = new Marked({
  renderer: {
    image(token) {
      const width = IMAGE_WIDTH_HINT.exec(token.title ?? "")?.[1]
      if (R.isNullish(width)) return false

      // Reuse marked's default image rendering (URL sanitizing, alt escaping) without the title
      const html = Renderer.prototype.image.call(this, { ...token, title: null })

      return html.replace(/^<img /, `<img width="${width}" `)
    },
  },
})

/**
 * Parse a release note's leading YAML-ish frontmatter block. Only the three fixed keys we author
 * are supported (`title`, `date`, `tags`) — intentionally not a general YAML parser. `tags` accepts
 * an inline list: `[a, b, c]`. Returns the parsed meta plus the remaining markdown body.
 */
function parseRelease(raw: string): { meta: unknown; body: string } {
  const match = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/.exec(raw)
  const frontmatter = match?.[1] ?? ""
  const body = match ? raw.slice(match[0].length) : raw

  const fields: Record<string, string> = {}
  for (const line of frontmatter.split(/\r?\n/)) {
    const sep = line.indexOf(":")
    if (sep === -1) continue
    fields[line.slice(0, sep).trim()] = line.slice(sep + 1).trim()
  }

  const parseTags = (value: string | undefined): string[] | undefined => {
    if (R.isNullish(value) || value === "") return undefined
    return value
      .replace(/^\[|\]$/g, "")
      .split(",")
      .map((tag) => tag.trim())
      .filter((tag) => tag !== "")
  }

  return {
    meta: {
      title: fields.title,
      date: fields.date,
      tags: parseTags(fields.tags),
    },
    body,
  }
}

/**
 * Parsed, validated and sorted once at module load (newest first). `html` is a cached lazy
 * getter so the markdown body is only rendered by marked when a consumer (the /releases page)
 * actually reads it — the sidebar chip only touches `meta.date`.
 */
// Releases dated in the future are not yet published — hide them until their day comes. A note dated
// today is published (same-day announcements are visible immediately).
// `meta.date` and `today` share the `YYYY-MM-DD` format, so a lexical compare is a date compare.
const today = toISODate(new Date())

const allReleases: ReleaseItem[] = Object.entries(modules)
  .flatMap(([path, raw]): ReleaseItem[] => {
    const { meta, body } = parseRelease(raw)
    const parsed = ReleaseMetaSchema.safeParse(meta)
    if (!parsed.success) {
      const message = `Invalid release frontmatter in ${path}: ${parsed.error.message}`
      if (import.meta.dev) throw new Error(message)
      console.error(message)
      return []
    }
    let html: string | undefined
    return [
      {
        slug: path.replace(/^.*\//, "").replace(/\.md$/, ""),
        label: parsed.data.title,
        meta: parsed.data,
        get html() {
          html ??= releaseMarked.parse(body, { async: false })
          return html
        },
      },
    ]
  })
  .filter((release) => release.meta.date <= today)
  .sort((a, b) => b.meta.date.localeCompare(a.meta.date))

const latestReleaseDate = allReleases[0]?.meta.date

export function useReleases() {
  /**
   * ISO day of the last visit to the release page, persisted in local storage. The default must
   * be `null`, not `undefined`: with `undefined` vueuse writes the literal string "undefined"
   * to storage on first read.
   */
  const lastVisit = useStorage<string | null>(StorageKeys.ReleasesLastVisit, null)

  /**
   * True when there is at least one published release (future publications are already filtered out
   * of {@link latestReleaseDate}) and the user has either never visited the release page or last
   * visited it before that most recent publication. A release dated today therefore goes unnoticed
   * by users who already opened the page earlier that same day — the marker is day-granular.
   */
  const hasUnseenRelease = computed<boolean>(() => {
    if (R.isNullish(latestReleaseDate)) return false

    return R.isNullish(lastVisit.value) || lastVisit.value < latestReleaseDate
  })

  /** Mark the release page as seen as of today (clears {@link hasUnseenRelease}). */
  const markReleasesVisited = () => {
    lastVisit.value = toISODate(new Date())
  }

  return { releases: allReleases, latestReleaseDate, hasUnseenRelease, markReleasesVisited }
}
